<script setup lang="ts">
import { provide, ref, watch, onMounted } from 'vue'
import Stage from '@/components/StageManager.vue'
import { useStageStore } from '@/stores/stage'
import { useUserStore } from '@/stores/user'
import { useSharedWorker } from '@/composables/useSharedworker'
import { useRoleWatcher } from '@/composables/useRoleWatcher'
import { auth } from './FirebaseConfig'
import { onAuthStateChanged, type User as FirebaseUser } from 'firebase/auth'
import { getToken } from './lib/authUtil'
import { useRoute, useRouter } from 'vue-router'
import { useWindowSize } from '@vueuse/core'
import SideMenu from '@/components/SideMenu.vue'
import GlobalNotification from './components/GlobalNotification.vue'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import WorkerStatusOverlay from '@/components/WorkerStatusOverlay.vue'
import ProfilePanel from '@/components/dev/ProfilePanel.vue'
import BulletIn from '@/components/BulletIn.vue'
import SyncNotificationBar from '@/components/SyncNotificationBar.vue'
import { useBulletInStore } from '@/stores/bulletIn'
import { storeToRefs } from 'pinia'
import * as Sentry from '@sentry/vue'
import { useGlobalKeyListener } from '@/composables/useGlobalKeyListener'
const stage = useStageStore()
const userStore = useUserStore()
const Worker = useSharedWorker()
const roleWatcher = useRoleWatcher()
const route = useRoute()
const router = useRouter()
const { width } = useWindowSize()

const mainContainer = ref<HTMLElement>()
provide('mainContainer', mainContainer)

const globalKeyListener = useGlobalKeyListener()
onMounted(() => {
  globalKeyListener.register(document)
})

/*
 * stage がある場合は Stage エリアを広げる
 * なくなった場合、即座に閉じるのではなく、一定時間後に閉じる
 */
const isSideOpen = ref(false)
const isSending = ref(false)
const handleIsSendingUpdate = (value: boolean) => {
  isSending.value = value
}

// モバイルパネルを閉じる
const closeMobilePanel = () => {
  isSideOpen.value = false
}

// モバイルパネルを開く
const openMobilePanel = () => {
  isSideOpen.value = true
}

// 変更内容の検出
const hasUnsavedChanges = ref(false)
const showTooltip = ref(false)
let tooltipTimer: NodeJS.Timeout | null = null

watch([stage.getAll(), isSending, width], () => {
  const hasStaging = () => stage.getModified().length > 0 || stage.getDeleted().length > 0
  const hasChanges = hasStaging() && !isSending.value

  hasUnsavedChanges.value = hasChanges

  // PCで未保存変更に基づいてサイドパネルを自動開閉
  if (width.value >= 768) {
    isSideOpen.value = hasChanges
  }

  if (hasChanges) {
    // 変更内容がある場合、ツールチップを表示
    showTooltip.value = true

    // 既存のタイマーをクリア
    if (tooltipTimer) {
      clearTimeout(tooltipTimer)
    }

    // 1秒後にツールチップを非表示（一度だけ）
    tooltipTimer = setTimeout(() => {
      showTooltip.value = false
      tooltipTimer = null
    }, 1000)
  } else {
    // 変更内容がない場合、ツールチップを非表示
    showTooltip.value = false
    if (tooltipTimer) {
      clearTimeout(tooltipTimer)
      tooltipTimer = null
    }
  }
})

onAuthStateChanged(auth, async (user: FirebaseUser | null) => {
  if (user) {
    useUserStore().updateAuthState(user)
    await callSync(user.refreshToken)
    roleWatcher.startWatching()
  } else {
    roleWatcher.stopWatching()
    // 現在のルートが認証を必要とする場合のみログアウト処理
    const currentRoute = router.currentRoute.value
    const requiresAuth = currentRoute.matched.some((route) => route.meta.requiresAuth)
    if (requiresAuth) {
      router.push('/logout')
    }
  }
})

async function callSync(refreshToken: string) {
  const token = await getTokenWithRetry()
  if (token) {
    Worker.initAndSync(token, refreshToken)
  } else {
    console.error('Failed to retrieve token, unable to initialize sync')
    Sentry.captureMessage('Failed to retrieve token, unable to initialize sync', 'error')
  }
}

async function getTokenWithRetry(maxRetries = 5, delay = 2000) {
  let retries = 0
  while (retries < maxRetries) {
    const token = getToken()
    if (token !== null) {
      return token
    }
    await new Promise((resolve) => setTimeout(resolve, delay))
    retries++
  }
  throw new Error('Failed to retrieve token')
}

const isRequiredAuthContent = () =>
  route.path !== '/login' &&
  route.path !== '/foreget_password' &&
  !route.path.startsWith('/reset_password')

const isSideMenuOpen = ref<boolean>(true)
const handleSideMenuChanged = (value: boolean) => {
  isSideMenuOpen.value = value
}

const { isBulletInAvailable } = storeToRefs(useBulletInStore())
</script>

<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
  <GlobalNotification />
  <div v-if="isRequiredAuthContent() && userStore.isLoggedIn" class="flex h-screen overflow-hidden">
    <BulletIn />
    <Suspense>
      <SideMenu id="sidemenu" @update:is-wide="handleSideMenuChanged" />
    </Suspense>

    <section
      id="main"
      ref="mainContainer"
      :class="[
        isSideMenuOpen ? 'left-[175px]' : 'left-[50px]',
        isSideOpen ? 'side-open' : '',
        isBulletInAvailable ? 'mt-11' : '',
        'md:left-0 md:top-0'
      ]"
      class="flex-1 overflow-y-auto md:relative"
    >
      <Suspense>
        <StackRootView />
      </Suspense>
      <!-- Stack UI Area  -->
      <div id="drawer_wrapper" :class="isBulletInAvailable ? 'mt-11' : ''">
        <StackView />
      </div>
    </section>

    <section id="side" :class="[isSideOpen ? 'side-open' : '', isBulletInAvailable ? 'mt-11' : '']">
      <Stage @update:is-sending="handleIsSendingUpdate" @close-mobile-panel="closeMobilePanel" />
    </section>

    <!-- モバイル用開くボタン -->
    <div
      class="fixed right-0 z-[1001] md:hidden"
      style="top: calc(50% + 24px); transform: translateY(-50%)"
    >
      <button
        v-if="!isSideOpen && hasUnsavedChanges"
        class="relative flex h-12 w-10 items-center justify-center rounded-l-lg border border-gray-200 bg-white text-gray-600 shadow-md transition-all duration-150 ease-out hover:bg-gray-50 hover:shadow-lg"
        @click="openMobilePanel"
      >
        <span
          class="material-symbols-outlined notranslate text-lg"
          translate="no"
          aria-hidden="true"
          >save</span
        >

        <!-- 未保存の変更内容通知点 -->
        <div
          v-if="hasUnsavedChanges"
          class="absolute -right-1 -top-1 size-3 animate-pulse rounded-full bg-red-500"
        ></div>
      </button>

      <!-- ツールチップ -->
      <Transition
        enter-active-class="transition-opacity duration-300 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-300 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showTooltip && !isSideOpen"
          class="absolute right-12 top-1/2 z-[1002] -translate-y-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white"
        >
          {{ $t('ui.unsavedChanges') }}
          <div
            class="absolute left-full top-1/2 size-0 -translate-y-1/2 border-y-2 border-l-4 border-y-transparent border-l-gray-800"
          ></div>
        </div>
      </Transition>
    </div>
    <LoadingOverlay class="overlay" />
    <WorkerStatusOverlay class="overlay" />
  </div>
  <div v-else>
    <RouterView />
  </div>
  <SyncNotificationBar />

</template>

<style scoped>
section#main #drawer_wrapper {
  z-index: 10;
}
#sidemenu {
  z-index: 100;
}
.overlay {
  z-index: 1000;
}
section#main {
  width: v-bind('isSideMenuOpen === true ? "calc(100% - 175px)" : "calc(100% - 50px)"');
}

@media (max-width: 768px) {
  section#main {
    width: 100%;
    left: 0;
  }
}
section#main.side-open {
  width: v-bind(
    'isSideMenuOpen === true ? "calc(100% - 175px - 20vw)" : "calc(100% - 50px - 20vw)"'
  );
}

section#main #drawer_wrapper {
  position: fixed;
  pointer-events: none;
  top: 0;
  width: v-bind(
    'isSideMenuOpen === true ? "calc(100% - 175px)" : "calc(100% - 50px)"'
  ); /* 100% - width of header */
  height: v-bind(
    'isBulletInAvailable === true ? "calc(100% - 2.75rem)" : "100%"'
  ); /* 100% - height of bulletIn */
  overflow-y: hidden;
  transition: all 0.5s ease 0s;
}

@media (max-width: 768px) {
  section#main #drawer_wrapper {
    width: 100%;
    left: 0;
    top: 48px; /* モバイルヘッダーの高さ分下に移動 */
    height: calc(100% - 48px); /* ヘッダーの高さ分高さを減少 */
  }
}

section#main.side-open #drawer_wrapper {
  width: v-bind(
    'isSideMenuOpen === true ? "calc(100% - 175px - 20vw)" : "calc(100% - 50px - 20vw)"'
  ); /* 100% - width of header - width of side */
}

@media (max-width: 768px) {
  section#main.side-open #drawer_wrapper {
    width: 100%;
    left: 0;
    top: 48px; /* モバイルヘッダーの高さ分下に移動 */
    height: calc(100% - 48px); /* ヘッダーの高さ分高さを減少 */
  }
}

section#side {
  display: flex;
  flex-shrink: 0;
  width: 0;
  transform: translateX(20vw);
  transition: all 0.5s ease 0s;
  background-color: #f3f4f6;
  visibility: hidden;
}

section#side.side-open {
  width: 20vw;
  transform: translateX(0);
  visibility: visible;
  overflow: hidden;
}

/* モバイルでStageManagerをGoogleサイトパネルのように実装 */
@media (max-width: 768px) {
  section#side {
    position: fixed !important;
    top: 48px !important; /* モバイルヘッダーの下 */
    right: 0 !important;
    width: 0 !important;
    height: calc(100% - 48px) !important; /* ヘッダーを除いた高さ */
    transform: translateX(100%) !important; /* 右側に隠す */
    z-index: 1000 !important; /* 最前面に表示 */
    background-color: white !important;
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15) !important;
    border-left: 1px solid #e5e7eb !important;
    transition:
      transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
      width 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }

  section#side.side-open {
    width: 300px !important; /* 固定幅 */
    transform: translateX(0) !important; /* 表示 */
  }
}

section#side #tag {
  position: absolute;
  height: 20px;
  font-size: 15px;
  line-height: 1;
  padding: 0;
  width: 100px;
  text-align: center;
  transform-origin: 0 0;
  margin-top: 100px;
  transform: rotate(90deg);
  background-color: #ccc;
  cursor: pointer;
}
</style>

<style src="./assets/main.css"></style>
<style src="./assets/assign-grid.css"></style>
<style src="./assets/markdown.swiss.css"></style>
<style src="./assets/markdown-alert.css"></style>
